// The list toolbar's controls, the team strip, the row menu and the bulk bar (Renewals board; Chip,
// Menu, Checkbox and Table cards). Popovers close on outside click, Esc and page scroll (spec C7.3).
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { IncreaseRule, RenewalRow } from '../auth/api';
import type { Role } from '../roles';
import { managesDepartment } from '../roles';
import { PlaceholderButton } from '../shell/Placeholder';
import { usePopover } from '../shell/use-popover';
import { Tip } from '../shell/Tip';
import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronsInIcon,
  ChevronsOutIcon,
  DotsIcon,
  DownloadIcon,
  ExpandIcon,
  ExternalLinkIcon,
  EyeIcon,
  FilterLinesIcon,
  NoteIcon,
  PlusIcon,
  SendIcon,
  SortDownIcon,
  SortUpIcon,
  SwapIcon,
  UserCheckIcon,
  TrashIcon,
  UndoIcon,
  XIcon,
} from '../shell/icons';
import {
  closedPresets,
  closedRangeLabel,
  customDays,
  customError,
  outcomeOptions,
  typedDay,
  type ClosedPreset,
  type ClosedRange,
} from './closed-lines';
import {
  carrierOf,
  cleared,
  expiresOptions,
  filterCount,
  filtersByPerson,
  lineLabel,
  premiumChangeNote,
  sortOptions,
  teamTip,
  UNASSIGNED,
  viewLabels,
  type GroupMode,
  type ListState,
  type TeamChip,
  type ViewKey,
} from './list-view';
import { offersHand, worksRow } from './handoff-lines';
import { maxReassign } from './reassign-lines';
import { stageOf, type Page, type TabKey } from './renewal-lines';

const groupLabels: Record<GroupMode, string> = {
  none: 'None',
  carrier: 'Carrier',
  person: 'Person',
};

/** "Group by Carrier" with its menu: None, Carrier, and Person for the Manager. */
export function GroupByChip({
  value,
  options,
  onChange,
}: {
  value: GroupMode;
  options: GroupMode[];
  onChange: (v: GroupMode) => void;
}) {
  const { open, setOpen, ref } = usePopover();
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className={value === 'none' ? 'chip' : 'chip soft'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="muted">Group by</span>
        <span className="ink">{groupLabels[value]}</span>
        <ChevronDownIcon className="ico s12 muted" />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="pop-scrim"
            aria-label="Close group by"
            onClick={() => setOpen(false)}
          />
          <div className="menu" role="menu" aria-label="Group by">
            {options.map((o) => (
              <button
                key={o}
                type="button"
                className="mi"
                role="menuitemradio"
                aria-checked={o === value}
                onClick={() => {
                  onChange(o);
                  setOpen(false);
                }}
              >
                {o === value ? <CheckIcon className="ico ck" /> : <span className="sp" />}
                {groupLabels[o]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function CollapseChip({ allClosed, onClick }: { allClosed: boolean; onClick: () => void }) {
  return (
    <button type="button" className="chip" onClick={onClick}>
      {allClosed ? <ChevronsOutIcon className="ico s14" /> : <ChevronsInIcon className="ico s14" />}
      {allClosed ? 'Expand all' : 'Collapse all'}
    </button>
  );
}

/** "View Default" with the built-in views; the ones that need data not in the portal yet wait. */
export function ViewChip({
  value,
  onPick,
}: {
  value: ViewKey;
  onPick: (v: 'default' | 'expiry') => void;
}) {
  const { open, setOpen, ref } = usePopover();
  const item = (key: 'default' | 'expiry') => (
    <button
      type="button"
      className="mi"
      role="menuitemradio"
      aria-checked={value === key}
      onClick={() => {
        onPick(key);
        setOpen(false);
      }}
    >
      {value === key ? <CheckIcon className="ico ck" /> : <span className="sp" />}
      {viewLabels[key]}
    </button>
  );
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="chip"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="muted">View</span>
        <span className="ink">{viewLabels[value]}</span>
        <ChevronDownIcon className="ico s12 muted" />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="pop-scrim"
            aria-label="Close views"
            onClick={() => setOpen(false)}
          />
          <div className="menu" role="menu" aria-label="Saved views">
            <div>
              <div className="vm-sec">Views</div>
              {item('default')}
              {item('expiry')}
              <PlaceholderButton id="list.view.bank" className="mi" block>
                <span className="sp" />
                Needs bank
              </PlaceholderButton>
              <PlaceholderButton id="list.view.pay" className="mi" block>
                <span className="sp" />
                Pay-to-renew carriers
              </PlaceholderButton>
              <div className="vm-div" />
              <PlaceholderButton id="list.view.save" className="mi" block>
                <PlusIcon />
                Save view
              </PlaceholderButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const toggle = (list: string[], v: string) =>
  list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

/**
 * Filters (spec C7.3): carrier, line, expires within, intake, premium change, handled by (Manager, AE),
 * Unassigned only and Missed (Manager), sort. Choices wait in a draft until Apply.
 */
export function FiltersButton({
  state,
  rows,
  role,
  tab,
  rule,
  onApply,
}: {
  state: ListState;
  /** The rows the options and their counts come from (the tab or stat card, before filters). */
  rows: RenewalRow[];
  role: Role;
  tab: TabKey | null;
  /** The stored increase rule, for the Premium change option and its note. */
  rule: IncreaseRule;
  onApply: (s: ListState) => void;
}) {
  const { open, setOpen, ref } = usePopover();
  const [draft, setDraft] = useState(state);
  const [carrierQ, setCarrierQ] = useState('');
  const [sortOpen, setSortOpen] = useState(false);
  const [room, setRoom] = useState<number>();
  const n = filterCount(state);
  const manager = managesDepartment(role);

  const start = () => {
    setDraft(state);
    setCarrierQ('');
    setSortOpen(false);
    // The dialog opens 38 below the button (kit .pop) and is held to the room left below it, with
    // the kit's margin of 12, so Apply and Reset stay on screen and the body scrolls instead.
    const box = ref.current?.getBoundingClientRect();
    if (box) setRoom(Math.max(240, window.innerHeight - box.top - 38 - 12));
    setOpen(!open);
  };
  const set = (changes: Partial<ListState>) => setDraft((d) => ({ ...d, ...changes }));

  const carriers = [...new Set(rows.map(carrierOf))].sort();
  const shownCarriers = carriers.filter((c) =>
    c.toLowerCase().includes(carrierQ.trim().toLowerCase()),
  );
  const lines = [...new Set(rows.map((r) => r.line))].sort();
  const people = [
    ...new Map(rows.filter((r) => r.handledBy).map((r) => [r.handledBy!.id, r.handledBy!.name])),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const option = (label: ReactNode, on: boolean, pick: () => void, key: string) => (
    <button
      key={key}
      type="button"
      className={on ? 'chip on' : 'chip'}
      aria-pressed={on}
      onClick={pick}
    >
      {label}
    </button>
  );
  const sortLabel =
    sortOptions.find((o) => o.key === draft.sort && o.key !== 'days')?.label ??
    (draft.sort === 'days' && draft.dir === 'asc' ? sortOptions[0].label : 'Column order');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className={open || n > 0 ? 'chip soft' : 'chip'}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={start}
      >
        <FilterLinesIcon className="ico s14" />
        Filters
        {n > 0 && <span className="badge">{n}</span>}
      </button>
      {open && (
        <>
          <button
            type="button"
            className="pop-scrim"
            aria-label="Close filters"
            onClick={() => setOpen(false)}
          />
          <div
            className="pop fpop"
            role="dialog"
            aria-labelledby="flt-t"
            style={{ maxHeight: room }}
          >
            <div className="pop-h">
              <span id="flt-t" className="t14 w6">
                Filters
              </span>
              <button
                type="button"
                className="xbtn"
                aria-label="Close filters"
                onClick={() => setOpen(false)}
              >
                <XIcon />
              </button>
            </div>
            <div className="pop-b">
              <div>
                <label className="flab" htmlFor="flt-cq">
                  Carrier
                </label>
                <input
                  id="flt-cq"
                  className="input"
                  type="search"
                  placeholder="Search carriers"
                  value={carrierQ}
                  onChange={(e) => setCarrierQ(e.target.value)}
                />
                <div className="clist">
                  {shownCarriers.map((c) => (
                    <label key={c} className="crow">
                      <input
                        type="checkbox"
                        checked={draft.carriers.includes(c)}
                        onChange={() => set({ carriers: toggle(draft.carriers, c) })}
                      />
                      <span>{c}</span>
                      <span className="cnt">{rows.filter((r) => carrierOf(r) === c).length}</span>
                    </label>
                  ))}
                  {shownCarriers.length === 0 && (
                    <div className="crow t12 muted">No carriers match</div>
                  )}
                </div>
              </div>
              <div role="group" aria-labelledby="flt-l">
                <div id="flt-l" className="flab">
                  Line
                </div>
                <div className="fopts">
                  {lines.map((l) =>
                    option(
                      lineLabel(l),
                      draft.lines.includes(l),
                      () => set({ lines: toggle(draft.lines, l) }),
                      l,
                    ),
                  )}
                </div>
              </div>
              {tab !== 'closed' && (
                <div role="group" aria-labelledby="flt-e">
                  <div id="flt-e" className="flab">
                    Expires
                  </div>
                  <div className="fopts">
                    {expiresOptions.map((o) =>
                      option(
                        o.label,
                        draft.expires === o.value,
                        () => set({ expires: o.value }),
                        o.value,
                      ),
                    )}
                  </div>
                </div>
              )}
              {tab !== 'closed' && (
                <div role="group" aria-labelledby="flt-i">
                  <div id="flt-i" className="flab">
                    Intake
                  </div>
                  <div className="fopts">
                    {['Download', 'Email', 'Online'].map((l) => (
                      <PlaceholderButton key={l} id="list.filter.intake" className="chip">
                        {l}
                      </PlaceholderButton>
                    ))}
                  </div>
                </div>
              )}
              {tab === 'closed' && (
                <div role="group" aria-labelledby="flt-o">
                  <div id="flt-o" className="flab">
                    Outcome
                  </div>
                  <div className="fopts">
                    {outcomeOptions.map((o) =>
                      option(
                        o.label,
                        draft.outcomes.includes(o.key),
                        () => set({ outcomes: toggle(draft.outcomes, o.key) }),
                        o.key,
                      ),
                    )}
                  </div>
                </div>
              )}
              <div role="group" aria-labelledby="flt-c">
                <div id="flt-c" className="flab">
                  Premium change
                </div>
                <div className="fopts">
                  {option(
                    'Any',
                    draft.premiumChange === 'any',
                    () => set({ premiumChange: 'any' }),
                    'any',
                  )}
                  {option(
                    'At or over the increase rule',
                    draft.premiumChange === 'over',
                    () => set({ premiumChange: 'over' }),
                    'over',
                  )}
                  {option(
                    'Increases to look over',
                    draft.premiumChange === 'review',
                    () => set({ premiumChange: 'review' }),
                    'review',
                  )}
                </div>
                <div className="fnote">{premiumChangeNote(rule)} Set in Settings, Defaults.</div>
              </div>
              {filtersByPerson(role) && (
                <div role="group" aria-labelledby="flt-b">
                  <div id="flt-b" className="flab">
                    Handled by
                  </div>
                  <div className="fopts">
                    {people.map(([id, name]) =>
                      option(
                        name,
                        draft.people.includes(id),
                        () => set({ people: toggle(draft.people, id) }),
                        id,
                      ),
                    )}
                    {option(
                      'Unassigned',
                      draft.people.includes(UNASSIGNED),
                      () => set({ people: toggle(draft.people, UNASSIGNED) }),
                      UNASSIGNED,
                    )}
                  </div>
                </div>
              )}
              {manager && (
                <div role="group" aria-labelledby="flt-q">
                  <div id="flt-q" className="flab">
                    Quick
                  </div>
                  <div className="fopts">
                    {option(
                      'Unassigned only',
                      draft.unassignedOnly,
                      () => set({ unassignedOnly: !draft.unassignedOnly }),
                      'un',
                    )}
                    {option('Missed', draft.missed, () => set({ missed: !draft.missed }), 'missed')}
                    {option(
                      'Overdue handoffs',
                      draft.overdue,
                      () => set({ overdue: !draft.overdue }),
                      'overdue',
                    )}
                  </div>
                  <div className="fnote">
                    Missed: expired in the last 30 days and not closed. Overdue handoffs: past the
                    Act by date, waiting or with the AE.
                  </div>
                </div>
              )}
              <div>
                <span className="flab" id="flt-sort-l">
                  Sort by
                </span>
                <div className="sel">
                  <button
                    type="button"
                    className="sel-b"
                    id="flt-sort"
                    aria-haspopup="listbox"
                    aria-expanded={sortOpen}
                    aria-labelledby="flt-sort-l flt-sort"
                    onClick={() => setSortOpen(!sortOpen)}
                  >
                    <span className="sel-v">{sortLabel}</span>
                    <ChevronDownIcon className="ico s12" />
                  </button>
                  {sortOpen && (
                    <div className="menu sel-m" role="listbox" aria-label="Sort by">
                      {sortOptions.map((o) => {
                        const on = draft.sort === o.key;
                        return (
                          <button
                            key={o.key}
                            type="button"
                            className="mi"
                            role="option"
                            aria-selected={on}
                            onClick={() => {
                              set({ sort: o.key, dir: o.key === 'change' ? 'desc' : 'asc' });
                              setSortOpen(false);
                            }}
                          >
                            <span className="dd-ck">{on && <CheckIcon className="ico s12" />}</span>
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="pop-f">
              <button
                type="button"
                className="linkbtn"
                style={{ textDecoration: 'none', color: 'var(--text2)' }}
                onClick={() => {
                  onApply(cleared(state));
                  setOpen(false);
                }}
              >
                Reset
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  onApply(draft);
                  setOpen(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** The Manager's team strip: a chip per person, clicking one filters the table to them. */
export function TeamStrip({
  team,
  person,
  onPick,
}: {
  team: TeamChip[];
  person: string | null;
  onPick: (id: string | null) => void;
}) {
  if (team.length === 0) return null;
  return (
    <div className="tstrip" role="group" aria-label="Filter by person">
      <span className="t12 muted">Team</span>
      {team.map((c) => {
        const on = person === c.id;
        return (
          <Tip key={c.id} text={teamTip(c)}>
            <button
              type="button"
              className={on ? 'pchip on' : 'pchip'}
              aria-pressed={on}
              onClick={() => onPick(on ? null : c.id)}
            >
              <span>{c.name}</span>
              <span className="pc-n">{c.open}</span>
              {c.attention > 0 && <span className="pc-u">{c.attention}</span>}
            </button>
          </Tip>
        );
      })}
    </div>
  );
}

/** The column header's sort button (Table card): the arrow shows on the active column. */
export function SortHeader({
  label,
  active,
  dir,
  onSort,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onSort: () => void;
}) {
  return (
    <div className="th">
      <button
        type="button"
        className={active ? 'sort active' : 'sort'}
        aria-label={`Sort by ${label}`}
        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
        onClick={onSort}
      >
        <span className="ell">{label}</span>
        {active && dir === 'desc' ? (
          <SortDownIcon className="ico si" />
        ) : (
          <SortUpIcon className="ico si" />
        )}
      </button>
    </div>
  );
}

/**
 * The row's "..." menu (Menu card, spec C7.3), only what the role may do: Quick view, Open record,
 * Reassign, Hand to AE, Add note, Open in AMS360 (Customer, Dec page: spec C13 has no Activity
 * link, decisions Oct 26), then Reopen (Manager, closed rows), then Delete
 * record (Manager, Admin) or Request delete (VA, AE) last, in red.
 */
export function RowMenu({
  row,
  role,
  meId,
  page,
  tab,
  onQuickView,
  onReassign,
  onHand,
  onAddNote,
  onSendBack = null,
  onPass = null,
  onTakeOver = null,
}: {
  row: RenewalRow;
  role: Role;
  meId: string;
  page: Page;
  tab: TabKey | null;
  onQuickView: () => void;
  onReassign: () => void;
  /** Hand to AE (spec C6): opens the dialog. */
  onHand: () => void;
  /** Every role that sees the row adds notes, closed rows too (spec C7.4). */
  onAddNote: () => void;
  /** Send back (spec C6), while the handoff waits on this AE; null otherwise. */
  onSendBack?: (() => void) | null;
  /** Pass to another AE, a handoff on this AE (S15 batch 7); null otherwise. */
  onPass?: (() => void) | null;
  /** Take over (the AE, decision 1): a confirm; null where they may not. */
  onTakeOver?: (() => void) | null;
}) {
  const { open, setOpen, ref } = usePopover();
  const [up, setUp] = useState(false);
  const [amsOpen, setAmsOpen] = useState(false);
  const manager = managesDepartment(role);
  const closed = stageOf(row) === 'closed';
  // A Renewal VA reassigns only what they handle; one with an AE is read only for them (spec C6).
  const canReassign =
    (worksRow(row, role, meId) || role === 'Account Executive' || manager) && !closed;
  const canHand = offersHand(row, role, meId);

  const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // Near the bottom of the window the menu opens upward so it stays on screen.
    const box = e.currentTarget.getBoundingClientRect();
    setUp(window.innerHeight - box.bottom < box.height * 12);
    setAmsOpen(false);
    setOpen(!open);
  };

  return (
    <div ref={ref} onClick={(e) => e.stopPropagation()} style={{ display: 'contents' }}>
      <button
        type="button"
        className="btn btn-sm btn-dots"
        aria-label={`More actions for ${row.insured}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggleMenu}
      >
        <DotsIcon className="ico s14" />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="pop-scrim"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          />
          <div
            className={up ? 'menu up' : 'menu'}
            role="menu"
            aria-label={`Actions for ${row.insured}`}
          >
            <button
              type="button"
              className="mi"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onQuickView();
              }}
            >
              <EyeIcon />
              Quick view
            </button>
            <Link
              className="mi"
              role="menuitem"
              to={`/records/${row.id}`}
              state={{ from: page, tab }}
            >
              <ExpandIcon />
              Open record
            </Link>
            {onTakeOver && (
              <button
                type="button"
                className="mi"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onTakeOver();
                }}
              >
                <UserCheckIcon />
                Take over
              </button>
            )}
            {canReassign && (
              <button
                type="button"
                className="mi"
                role="menuitem"
                aria-haspopup="dialog"
                onClick={() => {
                  setOpen(false);
                  onReassign();
                }}
              >
                <SwapIcon />
                Reassign
              </button>
            )}
            {canHand && (
              <button
                type="button"
                className="mi"
                role="menuitem"
                aria-haspopup="dialog"
                onClick={() => {
                  setOpen(false);
                  onHand();
                }}
              >
                <SendIcon />
                Hand to AE
              </button>
            )}
            {onSendBack && (
              <button
                type="button"
                className="mi"
                role="menuitem"
                aria-haspopup="dialog"
                onClick={() => {
                  setOpen(false);
                  onSendBack();
                }}
              >
                <UndoIcon />
                Send back
              </button>
            )}
            {onPass && (
              <button
                type="button"
                className="mi"
                role="menuitem"
                aria-haspopup="dialog"
                onClick={() => {
                  setOpen(false);
                  onPass();
                }}
              >
                <SendIcon />
                Pass to another AE
              </button>
            )}
            <button
              type="button"
              className="mi"
              role="menuitem"
              aria-haspopup="dialog"
              onClick={() => {
                setOpen(false);
                onAddNote();
              }}
            >
              <NoteIcon />
              Add note
            </button>
            <button
              type="button"
              className="mi"
              role="menuitem"
              aria-expanded={amsOpen}
              onClick={() => setAmsOpen(!amsOpen)}
            >
              <ExternalLinkIcon />
              Open in AMS360
              <ChevronDownIcon className={amsOpen ? 'ico chv open' : 'ico chv'} />
            </button>
            {amsOpen && (
              <>
                <a
                  className="mi subi"
                  role="menuitem"
                  href={row.ams.customer}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                >
                  Customer
                </a>
                <a
                  className="mi subi"
                  role="menuitem"
                  href={row.ams.decPage}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                >
                  Dec page
                </a>
              </>
            )}
            {manager && closed && (
              <>
                <div className="vm-div" />
                <PlaceholderButton id="list.row.reopen" className="mi" block>
                  <UndoIcon />
                  Reopen
                </PlaceholderButton>
              </>
            )}
            <div className="vm-div" />
            {manager ? (
              <PlaceholderButton id="list.row.delete" className="mi danger" block>
                <TrashIcon />
                Delete record
              </PlaceholderButton>
            ) : (
              <PlaceholderButton id="list.row.requestDelete" className="mi danger" block>
                <TrashIcon />
                Request delete
              </PlaceholderButton>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The bulk bar (spec C7.3): Reassign for the VA and the Manager, the Manager also Export; Accept for
 * the AE on Needs your answer.
 */
export function BulkBar({
  count,
  manager,
  onClear,
  onReassign,
  onAccept,
  onPass,
}: {
  count: number;
  manager: boolean;
  onClear: () => void;
  onReassign?: () => void;
  onAccept?: () => void;
  /** The AE's Pass to another AE, on Needs your answer and With me (S15 batch 7). */
  onPass?: () => void;
}) {
  if (count === 0) return null;
  return (
    <div className="bulk" role="region" aria-label="Bulk actions">
      <span className="w6">{count} selected</span>
      <button type="button" className="bb-link" onClick={onClear}>
        Clear
      </button>
      <span className="bb-sep" aria-hidden="true" />
      {onAccept && (
        <button type="button" className="bb" aria-haspopup="dialog" onClick={onAccept}>
          <CheckIcon className="ico s14" />
          Accept
        </button>
      )}
      {onPass && (
        <button type="button" className="bb" aria-haspopup="dialog" onClick={onPass}>
          <SendIcon className="ico s14" />
          Pass
        </button>
      )}
      {!onReassign ? null : count > maxReassign ? (
        // A limit, not unbuilt work: the server moves at most this many at once. As with
        // PlaceholderButton, the real disabled attribute and the tooltip on a focusable wrapper.
        <Tip text={`Select ${maxReassign} or fewer to reassign at once`} focusable>
          <button type="button" className="bb" disabled>
            <SwapIcon className="ico s14" />
            Reassign
          </button>
        </Tip>
      ) : (
        <button type="button" className="bb" aria-haspopup="dialog" onClick={onReassign}>
          <SwapIcon className="ico s14" />
          Reassign
        </button>
      )}
      {manager && (
        <PlaceholderButton id="list.bulk.export" className="bb">
          <DownloadIcon className="ico s14" />
          Export
        </PlaceholderButton>
      )}
    </div>
  );
}

/**
 * "Closed between" on the Closed tab (spec C7.3, Renewals board): the presets, or Custom with two
 * typed dates (kit Input; no native date picker). A preset applies at once; typed dates apply once
 * both make a range.
 */
export function ClosedRangeChip({
  range,
  onChange,
}: {
  range: ClosedRange;
  onChange: (r: { preset: ClosedPreset; from?: string; to?: string }) => void;
}) {
  const { open, setOpen, ref } = usePopover();
  const custom = range.preset === 'custom';
  const [from, setFrom] = useState(custom ? typedDay(range.from) : '');
  const [to, setTo] = useState(custom ? typedDay(range.to) : '');
  const error = customError(from, to);
  const typed = (f: string, t: string) => {
    setFrom(f);
    setTo(t);
    const days = customDays(f, t);
    if (days) onChange({ preset: 'custom', ...days });
  };
  return (
    <div ref={ref} className="rel">
      <button
        type="button"
        className="chip"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <CalendarIcon className="ico s14" />
        <span className="muted">Closed between</span>
        <span className="ink">{closedRangeLabel(range)}</span>
        <ChevronDownIcon className="ico s12" />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="pop-scrim"
            aria-label="Close date range"
            onClick={() => setOpen(false)}
          />
          <div className="pop" role="dialog" aria-labelledby="rng-t">
            <div className="pop-h">
              <span id="rng-t" className="t14 w6">
                Closed between
              </span>
              <button
                type="button"
                className="xbtn"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                <XIcon />
              </button>
            </div>
            <div className="pop-b">
              <div className="fopts" role="radiogroup" aria-label="Preset">
                {closedPresets.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={range.preset === p.key ? 'chip on' : 'chip'}
                    aria-pressed={range.preset === p.key}
                    onClick={() => {
                      if (p.key === 'custom') {
                        typed(from || typedDay(range.from), to || typedDay(range.to));
                        return;
                      }
                      onChange({ preset: p.key });
                      setOpen(false);
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="sf2">
                <div>
                  <label className="flab" htmlFor="rng-from">
                    From
                  </label>
                  <input
                    id="rng-from"
                    className="input"
                    placeholder="Oct 1, 2026"
                    autoComplete="off"
                    value={from}
                    onChange={(e) => typed(e.target.value, to)}
                  />
                </div>
                <div>
                  <label className="flab" htmlFor="rng-to">
                    To
                  </label>
                  <input
                    id="rng-to"
                    className="input"
                    placeholder="Oct 31, 2026"
                    autoComplete="off"
                    value={to}
                    onChange={(e) => typed(from, e.target.value)}
                  />
                </div>
              </div>
              {error ? (
                <p className="errl m0">{error}</p>
              ) : (
                <p className="fnote m0">Changing a date switches to Custom.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
